import { useCallback, useEffect, useMemo, useState } from 'react';

export interface UseIntersectionObserverOptions {
  threshold?: number | number[];
  root?: Element | null;
  rootMargin?: string;
}

export function useIntersectionObserver<T extends Element = Element>({
  threshold = 1,
  root = null,
  rootMargin = '0px',
}: UseIntersectionObserverOptions = {}): [
  (node: T | null) => void,
  IntersectionObserverEntry | null,
] {
  const [target, setTarget] = useState<T | null>(null);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);

  const ref = useCallback((node: T | null) => {
    setTarget(node);
  }, []);

  // Derive a stable string key from threshold so that inline array literals (e.g. [0, 0.5])
  // don't cause the observer to recreate on every render due to a changing reference.
  const thresholdKey = useMemo(
    () => (Array.isArray(threshold) ? threshold.join(',') : `${threshold}`),
    [threshold]
  );

  // Reconstruct the threshold value from the stable key so `threshold` (potentially unstable
  // array reference) is never a direct effect dependency.
  const stableThreshold = useMemo<number | number[]>(
    () =>
      thresholdKey.includes(',')
        ? thresholdKey.split(',').map(Number)
        : Number(thresholdKey),
    [thresholdKey]
  );

  useEffect(() => {
    if (!target) {
      setEntry(null);
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setEntry(null);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setEntry(entries[0] ?? null);
      },
      {
        threshold: stableThreshold,
        root,
        rootMargin,
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [target, stableThreshold, root, rootMargin]);

  return [ref, entry];
}
